// src/pages/VisitsDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Input, Table, message, Space, Typography, Modal, Upload, DatePicker } from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { fileAPI } from '../utils/api';
import { appendUploadToFormData, getDownloadFileName } from '../utils/fileUrls';
import { diaryAPI } from '../utils/api';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const VisitsDiary: React.FC = () => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [documentFile, setDocumentFile] = useState<UploadFile | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const normalizeDateValue = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (value?.format) return value.format('YYYY-MM-DD');
    return undefined;
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getVisitEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading visit entries:', error);
      message.error('Ошибка загрузки записей посещений');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
    setDocumentFile(null);
  };

  const openCreateModal = () => {
    setEditingId(null);
    form.setFieldsValue({ visit_date: dayjs() });
    setDocumentFile(null);
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      visit_date: record.visit_date ? dayjs(record.visit_date) : dayjs(),
      doctor_name: record.doctor_name,
      notes: record.notes,
    });
    setDocumentFile(null);
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      const formData = new FormData();
      const normalizedDate = normalizeDateValue(values.visit_date) || dayjs().format('YYYY-MM-DD');
      formData.append('visit_date', normalizedDate);
      formData.append('doctor_name', values.doctor_name);
      if (values.notes) {
        formData.append('notes', values.notes);
      }
      if (documentFile) {
        appendUploadToFormData(formData, 'document', documentFile, 'visit-document.bin');
      }

      if (editingId) {
        await diaryAPI.updateVisitEntry(editingId, formData);
        message.success('Запись о посещении успешно обновлена!');
      } else {
        await diaryAPI.createVisitEntry(formData);
        message.success('Запись о посещении успешно добавлена!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving visit entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Врач',
      dataIndex: 'doctor_name',
      key: 'doctor_name',
    },
    {
      title: 'Заметки',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: 'Документ',
      dataIndex: 'document_url',
      key: 'document_url',
      render: (url: string, record: any) => {
        if (!url) return '-';
        const handleDownload = async () => {
          try {
            const response = await fileAPI.downloadFile(url);
            const blob = new Blob([response.data as BlobPart]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getDownloadFileName(url, `visit_${record.visit_date || 'document'}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          } catch (error) {
            console.error('Error downloading file:', error);
            message.error('Ошибка скачивания файла');
          }
        };
        return (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Скачать
          </Button>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Дневник посещений врача</Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: isMobile ? 16 : 24 }}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342', width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Добавить посещение
          </Button>
          <Button
            onClick={async () => {
              try {
                const formData = new FormData();
                formData.append('visit_date', dayjs().format('YYYY-MM-DD'));
                formData.append('doctor_name', 'Визитов не было');

                await diaryAPI.createVisitEntry(formData);
                message.success('Отмечено, что визитов не было');
                await loadEntries();
              } catch (error: any) {
                console.error('Error saving no visits entry:', error);
                message.error(error.response?.data?.detail || 'Ошибка сохранения');
              }
            }}
            style={{ width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Визитов не было
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={entries}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: isMobile ? 10 : 20, simple: isMobile }}
            scroll={isMobile ? { x: 'max-content' } : undefined}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>
      </Card>

      <Modal
        title={editingId ? 'Редактировать посещение' : 'Добавить посещение'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Дата посещения"
            name="visit_date"
            rules={[{ required: true, message: 'Выберите дату!' }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" size="large" />
          </Form.Item>

          <Form.Item
            label="ФИО врача"
            name="doctor_name"
            rules={[{ required: true, message: 'Введите ФИО врача!' }]}
          >
            <Input placeholder="Иванов Иван Иванович" size="large" />
          </Form.Item>

          <Form.Item
            label="Заметки"
            name="notes"
          >
            <Input.TextArea rows={4} placeholder="Заметки о посещении..." />
          </Form.Item>

          <Form.Item label="Документ (выписка, опционально)">
            <Upload
              maxCount={1}
              accept="image/*,.pdf"
              beforeUpload={() => false}
              fileList={documentFile ? [documentFile] : []}
              onChange={({ file, fileList }) => {
                if (file.status === 'removed' || fileList.length === 0) {
                  setDocumentFile(null);
                  return;
                }
                setDocumentFile(fileList[fileList.length - 1]);
              }}
              onRemove={() => setDocumentFile(null)}
            >
              <Button icon={<UploadOutlined />}>Загрузить документ</Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingId ? 'Обновить' : 'Сохранить'}
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
